import { useEffect, useRef } from 'preact/hooks';

import { createSlotMount } from '../lib/slot-mount';

/**
 * P5.3 — "or pay over time" on the pay-in-full option, in the providers'
 * own words.
 *
 * Stripe's Payment Method Messaging Element (Matt, 2026-10-12): it shows
 * each provider's actual terms for this amount ("4 interest-free payments
 * of $X with Afterpay", "as low as $X/mo with Affirm") — accurate, and in
 * the wording the providers require. Our own "$X/mo" figure would be
 * neither: terms vary by customer, and BNPL providers restrict how amounts
 * may be advertised.
 *
 * Like the Payment Element it is a Stripe iframe, so it lives in the light
 * DOM and is laid out inside the widget through a <slot> (lib/slot-mount). Display only:
 * it decides nothing — eligibility is the server's (`deposit.financing`).
 * If Stripe cannot load, it renders nothing and the step works as before.
 */

interface MessagingElementLike {
  mount(el: HTMLElement): void;
  destroy(): void;
  update?(options: Record<string, unknown>): void;
}
interface StripeLike {
  elements(options?: Record<string, unknown>): {
    create(type: 'paymentMethodMessaging', options: Record<string, unknown>): MessagingElementLike;
  };
}

export interface FinancingMessageProps {
  publishableKey: string;
  /** The PAY-IN-FULL amount — what would be financed. */
  amountCents: number;
  currency: string;
  /** The provider list the server allowed for this amount. */
  methods: string[];
}

export function FinancingMessage({
  publishableKey,
  amountCents,
  currency,
  methods,
}: FinancingMessageProps) {
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<MessagingElementLike | null>(null);
  const methodsKey = methods.join(',');

  useEffect(() => {
    let cancelled = false;
    // In the widget's own layout via a <slot> (lib/slot-mount), so it
    // scrolls and clips with the summary instead of floating over it.
    const mount = createSlotMount(placeholderRef.current as HTMLDivElement, 'financing');
    const portal = mount.node;

    (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = (await loadStripe(publishableKey)) as unknown as StripeLike | null;
        if (cancelled || !stripe) return;
        const element = stripe.elements().create('paymentMethodMessaging', {
          amount: amountCents,
          currency: currency.toUpperCase(),
          paymentMethodTypes: methodsKey.split(','),
          countryCode: 'US',
        });
        elementRef.current = element;
        element.mount(portal);
      } catch {
        // Financing copy is a nicety; the payment step works without it.
      }
    })();

    return () => {
      cancelled = true;
      try {
        elementRef.current?.destroy();
      } catch {
        // already gone
      }
      elementRef.current = null;
      mount.remove();
    };
    // Rebuilt when what it describes changes — a promo moves the amount,
    // and the provider list follows it.
  }, [publishableKey, amountCents, currency, methodsKey]);

  return <div ref={placeholderRef} class="sbw-financing" aria-live="polite" style={{ minHeight: 4 }} />;
}
